/**
 * Canonical drug dosage frequency options for prescriptions.
 * Values are stored as-is for future persistence in the live DB;
 * labels match value for display in dropdowns.
 */

export interface DosageFrequencyOption {
  value: string
  label: string
  dosesPerDay: number
}

const RAW_FREQUENCIES = [
  'OD',
  'o.d.',
  'QD',
  'q.d.',
  'q1d',
  'q.1.d.',
  'SID',
  's.i.d.',
  'BD',
  'b.d.',
  'BID',
  'b.i.d.',
  'TDS',
  't.d.s.',
  'TID',
  't.i.d.',
  'TD',
  'QDS',
  'q.d.s.',
  'QID',
  'q.i.d.',
  'mane',
  'OM',
  'o.m.',
  'nocte',
  'ON',
  'o.n.',
  'HS',
  'h.s.',
  'qhs',
  'QHS',
  'qam',
  'QAM',
  'qpm',
  'QPM',
  'midi',
  'AM',
  'a.m.',
  'PM',
  'p.m.',
  'AC',
  'a.c.',
  'PC',
  'p.c.',
  'c̄',
  'c.',
  'a.c.h.s.',
  'ac&hs',
  'p.c.h.s.',
  'pc&hs',
  'q.h.',
  'qh',
  'q1h',
  'q2h',
  'q.2.h.',
  'q3h',
  'q4h',
  'q.4.h.',
  'q6h',
  'q.6.h.',
  'q8h',
  'q.8.h.',
  'q10h',
  'q12h',
  'q24h',
  'q48h',
  'q72h',
  'q4–6h',
  'q2–3h',
  'qqh',
  'q.q.h.',
  'omn. hor.',
  'omni hora',
  'omn. bih.',
  'omni bihora',
  'qod',
  'q.o.d.',
  'q.a.d.',
  'alt. d.',
  'alt. dieb.',
  'q2d',
  'q3d',
  'q4d',
  'q5d',
  'q7d',
  'q2–3d',
  'qw',
  'qwk',
  'QWK',
  'biw',
  'tiw',
  'q2w',
  'q3w',
  'q4w',
  'qmo',
  'q2mo',
  'q3mo',
  'q6mo',
  'qn',
  'qnly',
  'PRN',
  'p.r.n.',
  'SOS',
  's.o.s.',
  'STAT',
  'NOW',
  'ATC',
  'ad lib.',
]

/** Maps frequency value to doses per day for quantity calculation; unmapped values default to 1. */
const DOSES_PER_DAY_MAP: Record<string, number> = {
  BD: 2,
  'b.d.': 2,
  BID: 2,
  'b.i.d.': 2,
  TDS: 3,
  't.d.s.': 3,
  TID: 3,
  't.i.d.': 3,
  TD: 3,
  QDS: 4,
  'q.d.s.': 4,
  QID: 4,
  'q.i.d.': 4,
  'q2h': 12,
  'q.2.h.': 12,
  'q3h': 8,
  'q4h': 6,
  'q.4.h.': 6,
  'q6h': 4,
  'q.6.h.': 4,
  'q8h': 3,
  'q.8.h.': 3,
  'q10h': 2.4,
  'q12h': 2,
  'q.h.': 24,
  qh: 24,
  q1h: 24,
  'qqh': 24,
  'q.q.h.': 24,
  'omn. hor.': 24,
  'omni hora': 24,
  'omn. bih.': 12,
  'omni bihora': 12,
  'q4–6h': 5,
  'q2–3h': 8,
}

function getDosesPerDayForValue(value: string): number {
  return DOSES_PER_DAY_MAP[value] ?? 1
}

export const FREQUENCY_OPTIONS: DosageFrequencyOption[] = RAW_FREQUENCIES.map(
  (value) => ({
    value,
    label: value,
    dosesPerDay: getDosesPerDayForValue(value),
  })
)

export function getDosesPerDay(frequencyValue: string): number {
  const option = FREQUENCY_OPTIONS.find((f) => f.value === frequencyValue)
  return option?.dosesPerDay ?? 1
}
